import { CenterDropdownStyle } from '@elements/Dropdown';
import type { ShipmentDynamicFieldType } from '@hooks/shipment';
import { Check } from '@phosphor-icons/react';
import { CommonUtility } from '@utility/common';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@skupreme/shared/ui';

type BorderBoxProps = {
  className?: string;
  children?: ReactNode;
  readOnly?: boolean;
  selected?: boolean;
};

function BorderBox({
  className,
  children,
  readOnly,
  selected,
}: BorderBoxProps) {
  return (
    <div
      className={cn(
        'sk:relative sk:h-full sk:min-h-15 sk:rounded-[5px] sk:p-1.75',
        'sk:[&_.header]:mb-1.25 sk:[&_.header]:border-0 sk:[&_.header]:border-b sk:[&_.header]:border-solid sk:[&_.header]:border-border sk:[&_.header]:pr-3.75 sk:[&_.header]:font-bold',
        selected
          ? 'sk:border-2 sk:border-solid sk:border-primary'
          : 'sk:border sk:border-solid sk:border-border',
        readOnly && 'sk:cursor-not-allowed sk:bg-fill-quaternary',
        className,
      )}
    >
      {children}
    </div>
  );
}

function CardCheck({ children }: { children?: ReactNode }) {
  return (
    <div className="sk:absolute sk:right-1.75 sk:flex sk:size-3.5 sk:items-center sk:justify-center sk:rounded-[4px] sk:bg-success sk:p-0.5 sk:text-surface">
      {children}
    </div>
  );
}

type RadioDropdownProps = {
  item: ShipmentDynamicFieldType;
  readOnly?: boolean;
  value?: string;
  onChange?: (value: string) => void;
};

export function RadioDropdown({
  item,
  readOnly,
  value,
  onChange,
}: RadioDropdownProps) {
  const isSelected = useMemo(() => {
    if (!value) return false;

    if (CommonUtility.isValidArray(item.options)) {
      return !!item.options.find(x => x.value === value);
    }

    return value === item.value;
  }, [value, item]);

  if (readOnly) {
    return (
      <BorderBox readOnly>
        <div className="header">{item.label}</div>
        <div>{item.description}</div>
      </BorderBox>
    );
  }

  return (
    <BorderBox selected={isSelected}>
      {isSelected && (
        <CardCheck>
          <Check size={10} weight="bold" />
        </CardCheck>
      )}
      <div className="header">{item.label}</div>
      <div>{item.description}</div>
      <CenterDropdownStyle
        onChange={(_, { value }) => {
          onChange?.(value || '');
        }}
        selection
        options={item?.options as any[]}
      />
    </BorderBox>
  );
}
